import React, { useEffect, useState } from "react";
import Loader from "./Loader";
import { TailSpin } from "react-loader-spinner";
import Pagination from "./Pagination";
import NavBar from "./Navbar";
import TopNavBar from "./TopNavbar";
import moment from "moment";
import Search from "./Search";
import { fetchUsers, fetchUserCount } from "../services/UserService";
import "../styles/AdminPanel.css";
import "../styles/Dashboard.css";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import DashboardPeriodView from "./DashboardPeriodView";
import { formatPayPeriodRange } from "../utils/displayDate";
import {
  DashIconWrap,
  DollarHandIcon,
  DollarSignIcon,
  InvoiceDocIcon,
  InvoiceEditIcon,
  PeopleIcon,
} from "./DashboardIcons";
import { useAuth } from "../auth/auth";
import { isAdmin } from "../utils/rbac";
import { PERMISSIONS } from "../utils/rbac";
import {
  deniedActionClass,
  usePermissionGuard,
} from "../hooks/usePermissionGuard";
const apiUrl = process.env.REACT_APP_API_URL;

const EMPTY_INVOICE_STATS = {
  sentTotal: 0,
  sentCount: 0,
  draftTotal: 0,
  draftCount: 0,
};

function formatDashMoney(value) {
  const num = Number(value) || 0;
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPeriodLong(start, end) {
  return formatPayPeriodRange(start, end);
}

function formatPeriodShort(start, end) {
  if (!start || !end) return "—";
  return `${moment(start).format("MMM D").toUpperCase()} - ${moment(end)
    .format("MMM D")
    .toUpperCase()}`;
}

function toDateOnly(value) {
  if (!value) return "";
  return moment(value).format("YYYY-MM-DD");
}

async function fetchInvoiceStatsForRange(from, to) {
  if (!apiUrl || !from || !to) return { ...EMPTY_INVOICE_STATS };
  try {
    const res = await axios.get(`${apiUrl}/api/invoices`, {
      params: {
        page: 1,
        pageSize: 10000,
        status: "ALL",
        dateFrom: toDateOnly(from),
        dateTo: toDateOnly(to),
      },
    });
    const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
    const sent = rows.filter(
      (invoice) => String(invoice.status || "").toUpperCase() === "SENT"
    );
    const draft = rows.filter(
      (invoice) => String(invoice.status || "").toUpperCase() === "DRAFT"
    );
    const sumAmount = (list) =>
      list.reduce(
        (total, invoice) =>
          total + Number(invoice.total_amount ?? invoice.subtotal ?? 0),
        0
      );
    return {
      sentTotal: sumAmount(sent),
      sentCount: sent.length,
      draftTotal: sumAmount(draft),
      draftCount: draft.length,
      rows,
    };
  } catch (error) {
    return { ...EMPTY_INVOICE_STATS, rows: [] };
  }
}

function DashProgressBar({ percentage }) {
  const width = Math.max(0, Math.min(100, Number(percentage) || 0));
  return (
    <div className="dash-progress-track">
      <div className="dash-progress-fill" style={{ width: `${width}%` }} />
    </div>
  );
}

function PayPeriodCard({
  label,
  startDate,
  endDate,
  manageLabel,
  onManage,
  invoiceStats,
  totalUsers,
  pendingCount,
  generatedCount,
  percentage,
}) {
  const stats = invoiceStats || EMPTY_INVOICE_STATS;
  const roundedPct = Math.round(Number(percentage) || 0);

  return (
    <div className="dash-period-card">
      <div className="dash-period-header">
        <div>
          <p className="dash-period-label">{label}</p>
          <h2 className="dash-period-dates">
            {formatPeriodLong(startDate, endDate)}
          </h2>
        </div>
        <button type="button" className="dash-manage-btn" onClick={onManage}>
          {manageLabel}
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="dash-stats-row">
        <div className="dash-stat-card dash-stats-intro-card">
          <p className="dash-stats-intro-title">Invoice Stats</p>
          <p className="dash-stats-intro-desc">
            Overview of invoice generation for this period.
          </p>
        </div>
        <div className="dash-stat-card">
          <DashIconWrap>
            <DollarSignIcon />
          </DashIconWrap>
          <div className="dash-stat-copy">
            <p className="dash-stat-label">Total Earning</p>
            <p className="dash-stat-value">{formatDashMoney(stats.sentTotal)}</p>
          </div>
        </div>
        <div className="dash-stat-card">
          <DashIconWrap>
            <InvoiceDocIcon />
          </DashIconWrap>
          <div className="dash-stat-copy">
            <p className="dash-stat-label">Sent Invoice</p>
            <p className="dash-stat-value">{stats.sentCount} Invoices</p>
          </div>
        </div>
        <div className="dash-stat-card">
          <DashIconWrap>
            <DollarHandIcon />
          </DashIconWrap>
          <div className="dash-stat-copy">
            <p className="dash-stat-label">Draft Earning</p>
            <p className="dash-stat-value">
              {formatDashMoney(stats.draftTotal)}
            </p>
          </div>
        </div>
        <div className="dash-stat-card">
          <DashIconWrap>
            <InvoiceEditIcon />
          </DashIconWrap>
          <div className="dash-stat-copy">
            <p className="dash-stat-label">Draft Invoice</p>
            <p className="dash-stat-value">{stats.draftCount} Invoices</p>
          </div>
        </div>
      </div>

      <div className="dash-stats-row dash-stats-row--payslip">
        <div className="dash-stat-card dash-stats-intro-card">
          <p className="dash-stats-intro-title">Payslip Stats</p>
          <p className="dash-stats-intro-desc">
            Overview of payslip generation for this period.
          </p>
        </div>
        <div className="dash-stat-card">
          <DashIconWrap>
            <PeopleIcon />
          </DashIconWrap>
          <div className="dash-stat-copy">
            <p className="dash-stat-label">Total Employees</p>
            <p className="dash-stat-value">{totalUsers}</p>
          </div>
        </div>
        <div className="dash-stat-card">
          <DashIconWrap>
            <InvoiceEditIcon />
          </DashIconWrap>
          <div className="dash-stat-copy">
            <p className="dash-stat-label">Pending Payslips</p>
            <p className="dash-stat-value">{pendingCount}</p>
          </div>
        </div>
        <div className="dash-stat-card">
          <DashIconWrap>
            <InvoiceDocIcon />
          </DashIconWrap>
          <div className="dash-stat-copy">
            <p className="dash-stat-label">Generated Payslips</p>
            <p className="dash-stat-value">{generatedCount}</p>
          </div>
        </div>
        <div className="dash-progress-block">
          <div className="dash-progress-header">
            <div>
              <p className="dash-progress-title">Payslip Progress</p>
              <p className="dash-progress-desc">
                Track payslip generation for the current period.
              </p>
            </div>
            <span className="dash-progress-pct">{roundedPct}% Complete</span>
          </div>
          <DashProgressBar percentage={roundedPct} />
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [currentPagePrinted, setCurrentPagePrinted] = useState(1);
  const [pageSizePrinted] = useState(15);
  // const [startDateSent, setStartDateSent] = useState(null);
  const [totalPrintedUsers, setTotalPrintedUsers] = useState(0);
  const [showUserList, setShowUserList] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [rangeId, setRangeId] = useState(null);
  const [userListLoading, setUserListLoading] = useState(false);
  const [printedListLoading, setprintedListLoading] = useState(false);
  const [archivedListLoading, setarchivedListLoading] = useState(false);
  const [printedList, setPrintedList] = useState([]);
  const [archivedList, setArchivedList] = useState([]);
  const [totalArchivedUsers, setTotalArchivedUsers] = useState([]);
  const [currentPageArchived, setCurrentPageArchived] = useState(1);
  const [pageSizeArchived] = useState(15);
  const [rangeData, setRangeData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [percentageValue, setPercentageValue] = useState(0);
  const [previousRanges, setPreviousRanges] = useState([]);
  const [showAllPeriods, setShowAllPeriods] = useState(false);
  const [hasMorePrevious, setHasMorePrevious] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [currentActive, setCurrentActive] = useState("active");
  const [toBePrintedListCount, setToBePrintedListCount] = useState(0);
  const [bufferRange, setBufferRange] = useState(null);
  const [bufferPendingCount, setBufferPendingCount] = useState(0);
  const [bufferToBePrintedListCount, setBufferToBePrintedListCount] =
    useState(0);
  const [bufferPercentageValue, setBufferPercentageValue] = useState(0);
  const [, setTotalPayout] = useState(0);
  const [, setBufferTotalPayout] = useState(0);
  const [invoiceStats, setInvoiceStats] = useState(EMPTY_INVOICE_STATS);
  const [bufferInvoiceStats, setBufferInvoiceStats] =
    useState(EMPTY_INVOICE_STATS);
  const [periodInvoices, setPeriodInvoices] = useState([]);
  const [dashSearchQuery, setDashSearchQuery] = useState("");
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [isYtdModalOpen, setIsYtdModalOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [editedYtdAmount, setEditedYtdAmount] = useState("");
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null);
  const [pendingNameQuery, setPendingNameQuery] = useState("");
  const [printedNameQuery, setPrintedNameQuery] = useState("");
  const [archivedNameQuery, setArchivedNameQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStartDate = searchParams.get("rangeId");
  const mainTab = (searchParams.get("tab") || "payslips").toLowerCase();
  const childTab = (searchParams.get("view") || "all").toLowerCase();
  useAuth();
  const navigate = useNavigate();
  const { guardAction, PermissionDialog, isAllowed } = usePermissionGuard();
  const canDeleteInvoice = isAllowed(PERMISSIONS.DELETE);
  const canArchiveInvoice = isAllowed(PERMISSIONS.ARCHIVE);
  const canEditInvoice = isAllowed(PERMISSIONS.EDIT);
  const canManageYtd = isAllowed(PERMISSIONS.MANAGE_YTD);
  const canGeneratePdf = isAllowed(PERMISSIONS.GENERATE_PDF);

  const updateCurrentDate = async (startDateRange, otherRangesLimit) => {
    if (isAdmin()) {
      try {
        const response = await axios.get(`${apiUrl}/updateCurrentDate`);
        if (response.status !== 200) {
          console.error(`Unexpected status code: ${response.status}`);
        }
      } catch (error) {
        console.error("Failed to update current date:", error);
      }
    }

    await getRangeDateData(startDateRange, otherRangesLimit);
  };
  const getRangeDateData = async (
    startDateRange,
    otherRangesLimit,
    { silent } = {}
  ) => {
    try {
      if (!silent) setIsLoading(true);
      const payload = {};
      if (startDateRange) payload.startDate = startDateRange;
      if (otherRangesLimit != null) payload.otherRangesLimit = otherRangesLimit;
      const response = await axios.post(`${apiUrl}/getRangeDateData`, payload);
      const data = response.data;
      // console.log(data.currentRange);
      setRangeId(data.currentRange.id);
      setStartDate(data.currentRange.start_date);
      setEndDate(data.currentRange.end_date);
      setTotalUsers(data.currentRange.total_users);
      setCurrentActive(data.currentRange.current_active);
      setPendingCount(data.currentRange.pendingCount);
      setToBePrintedListCount(data.currentRange.toBePrintedListCount);
      setTotalPayout(Number(data.currentRange.totalPayout || 0));
      const percentage =
        data.currentRange.total_users > 0
          ? (data.currentRange.toBePrintedListCount /
              data.currentRange.total_users) *
            100
          : 0;
      // console.log(percentage);
      setPercentageValue(percentage.toFixed(2));

      const otherRanges = Array.isArray(data.otherRanges)
        ? data.otherRanges
        : [];

      // Compute Buffer range: most recent completed range whose end_date
      // is within the last 3 days from today (diffDays 1, 2, or 3).
      let candidate = null;

      if (otherRanges.length > 0) {
        const today = new Date();
        const todayDateOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );

        otherRanges.forEach((range) => {
          if (!range.end_date) return;
          const end = new Date(range.end_date);
          const endDateOnly = new Date(
            end.getFullYear(),
            end.getMonth(),
            end.getDate()
          );

          const diffMs = todayDateOnly - endDateOnly;
          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

          // Completed (end_date before today). For strict 3 days, use:
          if (diffDays > 0 && diffDays <= 3) {
            // if (diffDays > 0 /* && diffDays <= 3 */) {
            if (
              !candidate ||
              new Date(range.end_date) > new Date(candidate.end_date)
            ) {
              candidate = range;
            }
          }
        });
      }

      if (candidate) {
        const totalUsersBuffer = Number(candidate.total_users || 0);
        const totalInvoicesBuffer = Number(
          candidate.total_invoice_generated || 0
        );
        const pendingBuffer = Math.max(
          0,
          totalUsersBuffer - totalInvoicesBuffer
        );
        const toBePrintedBuffer = totalInvoicesBuffer;
        const percentageBuffer =
          totalUsersBuffer > 0
            ? ((toBePrintedBuffer / totalUsersBuffer) * 100).toFixed(2)
            : 0;

        setBufferRange(candidate);
        setBufferPendingCount(pendingBuffer);
        setBufferToBePrintedListCount(toBePrintedBuffer);
        setBufferPercentageValue(percentageBuffer);
        setBufferTotalPayout(Number(candidate.total_payout || 0));
      } else {
        setBufferRange(null);
        setBufferPendingCount(0);
        setBufferToBePrintedListCount(0);
        setBufferPercentageValue(0);
        setBufferTotalPayout(0);
      }

      // If we have a Buffer candidate, hide it from the Previous section
      const filteredPreviousRanges = candidate
        ? otherRanges.filter((range) => range.id !== candidate.id)
        : otherRanges;

      setPreviousRanges(filteredPreviousRanges);
      setHasMorePrevious(
        otherRangesLimit != null && otherRanges.length >= otherRangesLimit
      );
    } catch (error) {
      console.error("Failed to fetch range date data:", error);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };
  useEffect(() => {
    if (urlStartDate) {
      setShowAllPeriods(false);
      updateCurrentDate(urlStartDate, 8);
      setStartDate(urlStartDate);
      setShowUserList(true);
    } else {
      setShowUserList(false);
      updateCurrentDate("", 8);
    }
  }, [urlStartDate]);

  useEffect(() => {
    let cancelled = false;
    const loadInvoiceStats = async () => {
      if (startDate && endDate) {
        const stats = await fetchInvoiceStatsForRange(startDate, endDate);
        if (!cancelled) {
          setInvoiceStats({
            sentTotal: stats.sentTotal,
            sentCount: stats.sentCount,
            draftTotal: stats.draftTotal,
            draftCount: stats.draftCount,
          });
          setPeriodInvoices(stats.rows || []);
        }
      }
      if (bufferRange?.start_date && bufferRange?.end_date) {
        const stats = await fetchInvoiceStatsForRange(
          bufferRange.start_date,
          bufferRange.end_date
        );
        if (!cancelled) {
          setBufferInvoiceStats({
            sentTotal: stats.sentTotal,
            sentCount: stats.sentCount,
            draftTotal: stats.draftTotal,
            draftCount: stats.draftCount,
          });
        }
      } else if (!cancelled) {
        setBufferInvoiceStats(EMPTY_INVOICE_STATS);
      }
    };
    loadInvoiceStats();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, bufferRange]);

  useEffect(() => {
    if (!showUserList) return undefined;
    const timer = setTimeout(() => {
      const q = dashSearchQuery.trim();
      setPendingNameQuery(q);
      setPrintedNameQuery(q);
      setArchivedNameQuery(q);
      setCurrentPage(1);
      setCurrentPagePrinted(1);
      setCurrentPageArchived(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [dashSearchQuery, showUserList]);

  // useEffect(() => {
  //     getRangeDateData(startDate);
  // }, [startDate]);

  function formatInvoiceAdditions(additions) {
    const numericValue = Number(additions);
    if (Number.isNaN(numericValue)) {
      return "0.00";
    }
    return numericValue.toFixed(2);
  }

  function formatValue(value) {
    return value === "0.00" ? "-" : value;
  }

  const handleDeleteInvoice = async (id) => {
    guardAction(PERMISSIONS.DELETE, "delete invoice", async () => {
      const confirmed = window.confirm(
        "Are you sure you want to delete this invoice?"
      );
      if (!confirmed) return;

      setIsLoading(true);

      try {
        const response = await axios.post(`${apiUrl}/deleteGeneratedInvoice`, {
          invoiceId: id,
        });

        if (response.data && response.data.status === 1) {
          showMessage(response.data.message);
          window.location.reload();
        } else {
          showMessage("Error: " + response.data.message);
        }
      } catch (error) {
        showMessage("An unexpected error occurred");
        console.error("Delete invoice error:", error);
      } finally {
        setIsLoading(false);
      }
    });
  };

  const fetchPrintedUsers = async () => {
    if (!urlStartDate) {
      return;
    }
    if (rangeId == null) {
      return;
    }
    try {
      setprintedListLoading(true);
      const response = await axios.post(`${apiUrl}/getUsersWithInvoice`, {
        range_id: rangeId,
        page: currentPagePrinted,
        pageSize: pageSizePrinted,
        nameQuery: printedNameQuery,
      });

      setPrintedList(response.data.data);
      setTotalPrintedUsers(response.data.totalUsers); // Get total user count for pagination
    } catch (error) {
      console.error("Failed to fetch printed users:", error);
    } finally {
      setprintedListLoading(false);
    }
  };
  useEffect(() => {
    if (rangeId != null) {
      fetchPrintedUsers();
    }
  }, [currentPagePrinted, pageSizePrinted, rangeId, printedNameQuery]);

  const fetchArchivedUsers = async () => {
    if (!urlStartDate) {
      return;
    }
    if (rangeId == null) {
      return;
    }
    try {
      setarchivedListLoading(true);
      const response = await axios.post(
        `${apiUrl}/getUsersWithInvoiceArchived`,
        {
          range_id: rangeId,
          page: currentPageArchived,
          pageSize: pageSizeArchived,
          nameQuery: archivedNameQuery,
        }
      );

      setArchivedList(response.data.data);
      setTotalArchivedUsers(response.data.totalUsers); // Get total user count for pagination
    } catch (error) {
      console.error("Failed to fetch printed users:", error);
    } finally {
      setarchivedListLoading(false);
    }
  };

  const handleGenerateAllPdfs = () => {
    guardAction(PERMISSIONS.GENERATE_PDF, "generate PDFs", () => {
      navigate("/multipleInvoiceDetail?generate=archived&range_id=" + rangeId);
    });
  };

  useEffect(() => {
    if (rangeId != null) {
      fetchArchivedUsers();
    }
  }, [currentPageArchived, pageSizeArchived, rangeId, archivedNameQuery]);

  const handleBlockUser = async (email) => {
    try {
      await axios.post(`${apiUrl}/block`, { email });
      const updatedUsers = users.map((user) => {
        if (user.email === email) {
          // Toggle the 'blocked' status
          user.blocked = !user.blocked;
        }
        return user;
      });
      setUsers(updatedUsers);
    } catch (error) {
      //console.error("Failed to block/unblock user:", error);
    }
  };

  const generatePreviousViewItems = (otherRanges) => {
    return otherRanges.map((range) => {
      return (
      <div
        onClick={() => updateRange(range.start_date)}
        key={range.id}
          className="dash-previous-card"
        >
          <p className="dash-previous-date">
            {formatPeriodShort(range.start_date, range.end_date)}
          </p>
          <h3 className="dash-previous-title">Period {range.id}</h3>
          <div className="dash-previous-metrics">
            <div>
              <p className="dash-previous-metric-label">Invoice Value</p>
              <p className="dash-previous-metric-value">
                {formatDashMoney(range.total_payout)}
              </p>
        </div>
            <div>
              <p className="dash-previous-metric-label">Payslips Generated</p>
              <p className="dash-previous-metric-value">
                {range.total_invoice_generated || 0}/{range.total_users || 0}
              </p>
        </div>
      </div>
          <span className="dash-view-details">View Details →</span>
        </div>
      );
    });
  };

  const handleDeleteUser = async (email) => {
    const confirmed = true;
    // const confirmed = window.confirm(
    //   `Are you sure you want to delete the user ${email}?`
    // );
    if (confirmed) {
      try {
        await axios.delete(`${apiUrl}/users/${email}`);
        // Update the users state by filtering out the deleted user
        const updatedUsers = users.filter((user) => user.email !== email);
        setUsers(updatedUsers);
      } catch (error) {
        //console.error("Failed to delete user:", error);
      }
    }
  };

  const handleNext = () => {
    if (currentPage < Math.ceil(totalUsers / pageSize)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const showMessage = (text) => {
    setMessage(text);

    // Hide the message after 2000 milliseconds (2 seconds)
    setTimeout(() => {
      setMessage(null);
    }, 2000);
  };

  const updateRange = (rangeId) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("rangeId", rangeId);
    currentUrl.searchParams.set("tab", "payslips");
    currentUrl.searchParams.set("view", "all");
    window.location.href = currentUrl.toString();
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPrinted = () => {
    if (currentPagePrinted < Math.ceil(totalPrintedUsers / pageSizePrinted)) {
      setCurrentPagePrinted(currentPagePrinted + 1);
    }
  };

  const handlePreviousPrinted = () => {
    if (currentPagePrinted > 1) {
      setCurrentPagePrinted(currentPagePrinted - 1);
    }
  };

  const handleNextArchived = () => {
    console.log("hehrheh");
    if (
      currentPageArchived < Math.ceil(totalArchivedUsers / pageSizeArchived)
    ) {
      setCurrentPageArchived(currentPageArchived + 1);
    }
  };

  const handlePreviousArchived = () => {
    if (currentPageArchived > 1) {
      setCurrentPageArchived(currentPageArchived - 1);
    }
  };

  const handleShowAllUsers = () => {
    setShowAllUsers(true);
    setSearchResult([]);
  };

  const [count, setCount] = useState(0);

  const fetchPendingUsers = async () => {
    if (!urlStartDate) {
      return;
    }
    if (rangeId == null) {
      return;
    }
    try {
      setUserListLoading(true);
      const response = await axios.post(`${apiUrl}/getPendingUsers`, {
        range_id: rangeId, // Pass the correct range_id
        page: currentPage,
        pageSize: pageSize,
        nameQuery: pendingNameQuery,
      });
      console.log(response.data);
      setUsers(response.data.data);
      setCount(response.data.totalPendingUsers || 0);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    } finally {
      setUserListLoading(false);
    }
  };

  const handlePendingSearch = () => {
    setCurrentPage(1);
    fetchPendingUsers();
  };

  const handlePrintedSearch = () => {
    setCurrentPagePrinted(1);
    fetchPrintedUsers();
  };

  const handleArchivedSearch = () => {
    setCurrentPageArchived(1);
    fetchArchivedUsers();
  };

  useEffect(() => {
    if (rangeId != null) {
      fetchPendingUsers();
    }
  }, [currentPage, pageSize, rangeId, pendingNameQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleZeroSubmission = async (id) => {
    const confirmed = window.confirm(
      `Are you sure you want to omit this user from paylist?`
    );

    if (!confirmed) {
      return;
    }

    if (!id) {
      showMessage("Please select a user.");
      return;
    }
    if (!rangeId) {
      showMessage("Please select a range.");
      return;
    }

    const values = {
      userId: id,
      payDateId: rangeId,
    };

    setIsLoading(true);
    // console.log(values);

    try {
      const response = await axios.post(`${apiUrl}/newZeroInvoice`, values);
      const data = response.data;
      if (response.status === 200) {
        if (data.status === 1) {
          showMessage(data.message);
          const currentUrl = new URL(window.location.href);

          // console.log(urlStartDate);
          currentUrl.searchParams.set("rangeId", urlStartDate);
          window.location.href = currentUrl.toString();
        } else {
          showMessage("Error: " + data.message);
        }
      } else {
        showMessage("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error:", error);
      showMessage("An error occured");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditYtdSubmit = async () => {
    try {
      // Use the new updateYTDFinal endpoint - user enters final YTD, system calculates adjustment
      setIsLoading(true);
      const response = await axios.post(`${apiUrl}/updateYTDFinal`, {
        invoiceId: selectedInvoiceId,
        newYTDFinal: parseFloat(editedYtdAmount),
      });

      const data = response.data;
      if (response.status === 200) {
        if (data.status === 1) {
          // alert("YTD Final updated successfully!");

          setIsYtdModalOpen(false);
          fetchPrintedUsers();
          //  setTimeout(() => {
          //   fetchPrintedUsers();
          // }, 500);
        } else {
          showMessage("Error: " + data.message);
        }
        // Refresh data here if needed
      } else {
        showMessage("Error: " + data.message);
      }
    } catch (err) {
      console.error(err);
      console.error("Error:", err);
      showMessage("An error occured");
    } finally {
      setIsLoading(false);
    }
  };

  const archiveInvoice = async (id, type = "archive") => {
    const actionLabel =
      type === "unarchive" ? "unarchive invoice" : "archive invoice";

    guardAction(PERMISSIONS.ARCHIVE, actionLabel, async () => {
      if (!id) {
        showMessage("Please select a invoice.");
        return;
      }
      let title = "Are you sure you want to archive this invoice?";
      if (type == "unarchive") {
        title = "Are you sure you want to un-archive this invoice?";
      }

      const confirmed = window.confirm(title);

      if (!confirmed) {
        return;
      }

      const values = {
        invoiceId: id,
        type: type,
      };

      setIsLoading(true);

      try {
        const response = await axios.post(`${apiUrl}/archiveInvoice`, values);
        const data = response.data;
        if (response.status === 200) {
          if (data.status === 1) {
            showMessage(data.message);
            const currentUrl = new URL(window.location.href);

            currentUrl.searchParams.set("rangeId", urlStartDate);
            window.location.href = currentUrl.toString();
          } else {
            showMessage("Error: " + data.message);
          }
        } else {
          showMessage("Error: " + data.message);
        }
      } catch (error) {
        console.error("Error:", error);
        showMessage("An error occured");
      } finally {
        setIsLoading(false);
      }
    });
  };

  const handleShowUserList = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("rangeId", startDate);
    currentUrl.searchParams.set("tab", "payslips");
    currentUrl.searchParams.set("view", "all");
    window.location.href = currentUrl.toString();
  };
  const samePageRefresh = () => {
    setShowUserList(false);
    navigate("/dashboard");
  };

  return (
    <div className="admin-panel-parent-container">
      <PermissionDialog />
      <NavBar />
      <TopNavBar />
      {message && (
        <div className="custom-modal">
          <div className="modal-content">{message}</div>
        </div>
      )}
      {isYtdModalOpen && currentInvoiceData && (
        <div className="custom-modal">
          <div className="modal-content-ytd" style={{ minWidth: "400px" }}>
            <h3 style={{ marginBottom: "16px" }}>Edit YTD Amount</h3>

            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              YTD Amount:
            </label>
            <input
              type="number"
              step="0.01"
              value={editedYtdAmount}
              onChange={(e) => setEditedYtdAmount(e.target.value)}
              placeholder="Enter YTD amount"
              style={{
                width: "100%",
                padding: "10px",
                marginBottom: "16px",
                fontSize: "15px",
                border: "2px solid #2E8056",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                className="edit-ytd-submit"
                onClick={() => setIsYtdModalOpen(false)}
              >
                Cancel
              </button>
              <button className="edit-ytd-submit" onClick={handleEditYtdSubmit}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <div className="spinner-container">
          <TailSpin
            type="TailSpin"
            color="rgb(155, 236, 34)"
            height={70}
            width={70}
            timeout={5000}
          />
        </div>
      ) : (
        <div className="admin-card admin-panel dashboard-page">
          <div className="admin-card-body">
            {!showUserList && (
              <>
            <div className="dash-page-title" onClick={samePageRefresh}>
                    Dashboard
              </div>
            <PayPeriodCard
              label={
                currentActive === "active"
                  ? "Current Pay Period"
                  : "Previous Pay Period"
              }
              startDate={startDate}
              endDate={endDate}
              manageLabel="Manage Current Period"
              onManage={handleShowUserList}
              invoiceStats={invoiceStats}
              totalUsers={totalUsers}
              pendingCount={pendingCount}
              generatedCount={toBePrintedListCount}
              percentage={percentageValue}
            />
            {bufferRange && (
              <PayPeriodCard
                label="Buffer Pay Period"
                startDate={bufferRange.start_date}
                endDate={bufferRange.end_date}
                manageLabel="Manage Current Period"
                onManage={() => updateRange(bufferRange.start_date)}
                invoiceStats={bufferInvoiceStats}
                totalUsers={bufferRange.total_users}
                pendingCount={bufferPendingCount}
                generatedCount={bufferToBePrintedListCount}
                percentage={bufferPercentageValue}
              />
            )}
            <div className="dash-previous-section">
                <div className="dash-previous-header">
                  <h2 className="dash-previous-heading">
                    Previous Payroll Periods
                    <span className="dash-previous-sub">
                      Quickly access and review past payroll periods.
                    </span>
                  </h2>
                  {(hasMorePrevious || showAllPeriods) ? (
                  <button
                      type="button"
                      className="dash-view-all"
                      onClick={() => {
                        const next = !showAllPeriods;
                        setShowAllPeriods(next);
                        getRangeDateData(
                          urlStartDate || "",
                          next ? undefined : 8,
                          { silent: true }
                        );
                      }}
                    >
                      {showAllPeriods
                        ? "Show Less"
                        : "View All Periods →"}
                  </button>
                  ) : null}
                </div>
                {previousRanges.length > 0 ? (
                  <div className="dash-previous-grid">
                    {generatePreviousViewItems(previousRanges)}
                  </div>
                ) : (
                  <div className="dash-empty-previous">
                    No previous payroll periods yet.
                  </div>
                )}
              </div>
              </>
            )}
            {showUserList && (
              <DashboardPeriodView
                startDate={startDate}
                endDate={endDate}
                currentActive={currentActive}
                mainTab={mainTab === "invoices" ? "invoices" : "payslips"}
                childTab={childTab}
                onMainTab={(tab) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("tab", tab);
                    next.set("view", "all");
                    return next;
                  });
                }}
                onChildTab={(view) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("view", view);
                    return next;
                  });
                }}
                onBack={samePageRefresh}
                totalUsers={totalUsers}
                pendingCount={pendingCount}
                generatedCount={toBePrintedListCount}
                percentage={percentageValue}
                invoiceStats={invoiceStats}
                periodInvoices={periodInvoices}
                rangeId={rangeId}
                searchValue={dashSearchQuery}
                onSearchChange={setDashSearchQuery}
                onSearchSubmit={() => {
                  setPendingNameQuery(dashSearchQuery);
                  setPrintedNameQuery(dashSearchQuery);
                  setArchivedNameQuery(dashSearchQuery);
                  setCurrentPage(1);
                  setCurrentPagePrinted(1);
                  setCurrentPageArchived(1);
                }}
                users={users}
                userListLoading={userListLoading}
                currentPage={currentPage}
                pageSize={pageSize}
                pendingTotal={count}
                onPendingPage={setCurrentPage}
                onZeroSubmission={handleZeroSubmission}
                printedList={printedList}
                printedListLoading={printedListLoading}
                currentPagePrinted={currentPagePrinted}
                pageSizePrinted={pageSizePrinted}
                totalPrintedUsers={totalPrintedUsers}
                onPrintedPage={setCurrentPagePrinted}
                archivedList={archivedList}
                archivedListLoading={archivedListLoading}
                currentPageArchived={currentPageArchived}
                pageSizeArchived={pageSizeArchived}
                totalArchivedUsers={totalArchivedUsers}
                onArchivedPage={setCurrentPageArchived}
                formatInvoiceAdditions={formatInvoiceAdditions}
                formatValue={formatValue}
                formatDashMoney={formatDashMoney}
                formatPeriodLong={formatPeriodLong}
                archiveInvoice={archiveInvoice}
                handleDeleteInvoice={handleDeleteInvoice}
                handleGenerateAllPdfs={handleGenerateAllPdfs}
                onEditYtd={(invoice) => {
                  guardAction(PERMISSIONS.MANAGE_YTD, "edit YTD amount", () => {
                                      setIsYtdModalOpen(true);
                                      setSelectedInvoiceId(invoice.id);
                                      setCurrentInvoiceData(invoice);
                                      setEditedYtdAmount(
                                        invoice.YTDFinal || invoice.YTDAmount || 0
                                      );
                                  });
                                }}
                canArchiveInvoice={canArchiveInvoice}
                canEditInvoice={canEditInvoice}
                canDeleteInvoice={canDeleteInvoice}
                canManageYtd={canManageYtd}
                canGeneratePdf={canGeneratePdf}
                canCreatePayslip={isAllowed(PERMISSIONS.CREATE_PAYSLIP)}
                canCreateInvoice={isAllowed(PERMISSIONS.CREATE_INVOICE)}
                deniedActionClass={deniedActionClass}
                guardAction={guardAction}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
